<template>
  <section>
    <p class="muted">
      Abra com o gatilho (ex.: <strong>{{ displayWake }} oi</strong>). Enquanto a sessão estiver ativa, pode
      conversar sem repetir o nome. Encerre com <strong>tchau</strong> / <strong>fim</strong> ou
      espere o tempo de inatividade.
    </p>

    <form class="panel form" style="margin-top: 1rem" @submit.prevent="save" v-if="settings">
      <h2>Identidade</h2>
      <label>
        Nome da IA
        <input v-model="settings.name" required maxlength="64" />
      </label>
      <label>
        Palavra de acionamento (texto)
        <input v-model="settings.wakeWord" required maxlength="32" />
      </label>
      <label>
        Sessão ativa (minutos sem gatilho)
        <input
          v-model.number="settings.conversationSessionMinutes"
          type="number"
          min="1"
          max="120"
          step="1"
        />
      </label>
      <label class="check">
        <input v-model="settings.requireWakeWordForText" type="checkbox" />
        Exigir gatilho no texto (fora da sessão)
      </label>
      <label class="check">
        <input v-model="settings.audioAlwaysTriggers" type="checkbox" />
        Áudio sempre aciona a secretária
      </label>

      <h2>Modo segundo eu</h2>
      <label class="check warn">
        <input v-model="settings.secondSelfMode" type="checkbox" />
        Permitir tudo (navegação sem sites autorizados, agir como meu braço direito)
      </label>
      <p class="muted">
        Ligado: a {{ displayName }} navega em qualquer HTTPS, envia WhatsApp com mais liberdade e passa por
        ferramentas que normalmente pedem aprovação. Desligado: volta às restrições (allowlist de
        sites, contatos salvos, aprovações).
      </p>

      <h2>Motor da {{ displayName }}</h2>
      <p class="muted">
        Provedor e modelo valem nas <strong>próximas</strong> mensagens (sem reiniciar o servidor).
        Chaves ficam só no <code>.env</code> da VPS — nunca neste painel.
      </p>
      <label>
        Provedor de IA
        <select v-model="settings.aiProvider" @change="onProviderChange">
          <option
            v-for="p in aiCatalog"
            :key="p.id"
            :value="p.id"
            :disabled="!credentialOk(p.id)"
          >
            {{ p.label }}{{ credentialOk(p.id) ? '' : ' (chave ausente no .env)' }}
          </option>
        </select>
      </label>
      <p class="muted" v-if="settings.aiProvider === 'openai' && !aiCredentials?.openai.configured">
        Para usar OpenAI, defina <code>OPENAI_API_KEY</code> no <code>.env</code> e reinicie o
        container uma vez.
      </p>
      <p class="muted" v-if="settings.aiProvider === 'deepseek' && !aiCredentials?.deepseek.configured">
        Para usar DeepSeek, defina <code>AI_API_KEY</code> ou <code>DEEPSEEK_API_KEY</code> no
        <code>.env</code>.
      </p>
      <label>
        Modelo
        <select v-model="settings.aiModel" required @change="markCustom">
          <option v-for="m in modelsForProvider" :key="m.id" :value="m.id">
            {{ m.label }}
          </option>
        </select>
      </label>
      <p class="muted" v-if="selectedModelNote">{{ selectedModelNote }}</p>
      <p class="muted">
        Credenciais:
        DeepSeek {{ aiCredentials?.deepseek.configured ? '✓ configurada' : '✗ ausente' }} ·
        OpenAI {{ aiCredentials?.openai.configured ? '✓ configurada' : '✗ ausente' }}
      </p>
      <div class="preset-row">
        <button
          type="button"
          class="preset"
          :class="{ active: settings.aiPreset === 'economy' }"
          @click="applyPreset('economy')"
        >
          Econômico
        </button>
        <button
          type="button"
          class="preset"
          :class="{ active: settings.aiPreset === 'balanced' }"
          @click="applyPreset('balanced')"
        >
          Equilibrado
        </button>
        <button
          type="button"
          class="preset"
          :class="{ active: settings.aiPreset === 'max' }"
          @click="applyPreset('max')"
        >
          Máximo
        </button>
        <span class="badge" v-if="settings.aiPreset === 'custom'">Personalizado</span>
      </div>
      <label>
        Modo de esforço
        <select v-model="settings.aiEffortMode" @change="markCustom">
          <option value="auto">Automático (por tipo de mensagem)</option>
          <option value="fixed">Fixo (sempre o mesmo)</option>
        </select>
      </label>
      <label>
        Thinking (cadeia de raciocínio da API)
        <select
          v-model="settings.aiThinking"
          :disabled="settings.aiEffortMode === 'auto'"
          @change="markCustom"
        >
          <option value="enabled">Ligado</option>
          <option value="disabled">Desligado</option>
        </select>
      </label>
      <label>
        Esforço de raciocínio
        <select
          v-model="settings.aiReasoningEffort"
          :disabled="settings.aiEffortMode === 'auto' || settings.aiThinking === 'disabled'"
          @change="markCustom"
        >
          <option value="low">Baixo</option>
          <option value="high">Alto</option>
          <option value="max">Máximo</option>
        </select>
      </label>
      <label>
        Máx. tokens de saída
        <input
          v-model.number="settings.aiMaxTokens"
          type="number"
          min="256"
          max="8192"
          step="1"
          @change="markCustom"
        />
      </label>
      <label>
        Rodadas máximas de ferramentas
        <input
          v-model.number="settings.aiMaxToolRounds"
          type="number"
          min="2"
          max="15"
          step="1"
          @change="markCustom"
        />
      </label>
      <label>
        Temperatura
        <input
          v-model.number="settings.aiTemperature"
          type="number"
          min="0"
          max="2"
          step="0.1"
          :disabled="settings.aiThinking === 'enabled' && settings.aiEffortMode === 'fixed'"
          @change="markCustom"
        />
      </label>
      <p class="muted" v-if="settings.aiThinking === 'enabled' || settings.aiEffortMode === 'auto'">
        Com thinking ligado (DeepSeek) a API ignora temperatura. Em OpenAI + tools (ex.: gpt-6-luna),
        a {{ displayName }} força reasoning adequado automaticamente.
      </p>
      <label>
        Fuso horário
        <input v-model="settings.timezone" required />
      </label>
      <label>
        Números autorizados (um por linha, só dígitos)
        <textarea v-model="numbersText" rows="4" />
      </label>

      <div class="actions">
        <button type="submit" :disabled="saving">{{ saving ? 'Salvando…' : 'Salvar' }}</button>
        <p class="muted" v-if="message">{{ message }}</p>
      </div>
    </form>

    <section class="panel form" style="margin-top: 1.5rem" v-if="settings">
      <h2>Contatos WhatsApp</h2>
      <p class="muted">
        Cadastre nomes (ex.: Izolda) para a {{ displayName }} enviar mensagens. Marque
        <strong>enviar sem pedir</strong> para ela disparar na hora, sem confirmar com você.
      </p>
      <div class="contact-row">
        <input v-model="newContact.name" placeholder="Nome" maxlength="120" />
        <input v-model="newContact.number" placeholder="Número com DDI" maxlength="32" />
        <label class="check compact">
          <input v-model="newContact.autoSend" type="checkbox" />
          Enviar sem pedir
        </label>
        <button type="button" :disabled="savingContact" @click="addContact">
          {{ savingContact ? '…' : 'Adicionar' }}
        </button>
      </div>
      <ul class="contact-list" v-if="contacts.length">
        <li v-for="c in contacts" :key="c.id">
          <div>
            <strong>{{ c.name }}</strong>
            <span class="muted"> {{ c.number }}</span>
            <span v-if="c.autoSend" class="badge">sem pedir</span>
          </div>
          <div class="contact-actions">
            <button type="button" class="linkish" @click="toggleAutoSend(c)">
              {{ c.autoSend ? 'Exigir confirmação' : 'Enviar sem pedir' }}
            </button>
            <button type="button" class="linkish" @click="removeContact(c.id)">Remover</button>
          </div>
        </li>
      </ul>
      <p class="muted" v-else>Nenhum contato ainda.</p>
      <p class="muted" v-if="contactMessage">{{ contactMessage }}</p>
    </section>

    <p class="muted" v-else-if="error">{{ error }}</p>
    <p class="muted" v-else>Carregando configurações…</p>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { assistantName, setAssistantIdentity, wakeWord } from '../assistant-name';

type AiPreset = 'economy' | 'balanced' | 'max' | 'custom';

type AiProviderId = 'deepseek' | 'openai';

type EditableSettings = {
  name: string;
  wakeWord: string;
  requireWakeWordForText: boolean;
  audioAlwaysTriggers: boolean;
  conversationSessionMinutes: number;
  secondSelfMode: boolean;
  timezone: string;
  aiProvider: AiProviderId;
  aiModel: string;
  aiTemperature: number;
  aiMaxTokens: number;
  aiPreset: AiPreset;
  aiThinking: 'enabled' | 'disabled';
  aiReasoningEffort: 'low' | 'high' | 'max';
  aiEffortMode: 'auto' | 'fixed';
  aiMaxToolRounds: number;
  authorizedNumbers: string[];
};

type AiCatalogEntry = {
  id: AiProviderId;
  label: string;
  defaultModel: string;
  models: Array<{ id: string; label: string; notes?: string }>;
};

const PRESETS: Record<Exclude<AiPreset, 'custom'>, Partial<EditableSettings>> = {
  economy: {
    aiPreset: 'economy',
    aiThinking: 'disabled',
    aiReasoningEffort: 'low',
    aiEffortMode: 'fixed',
    aiMaxTokens: 1024,
    aiMaxToolRounds: 3,
  },
  balanced: {
    aiPreset: 'balanced',
    aiThinking: 'enabled',
    aiReasoningEffort: 'high',
    aiEffortMode: 'auto',
    aiMaxTokens: 2048,
    aiMaxToolRounds: 5,
  },
  max: {
    aiPreset: 'max',
    aiThinking: 'enabled',
    aiReasoningEffort: 'high',
    aiEffortMode: 'fixed',
    aiMaxTokens: 4096,
    aiMaxToolRounds: 8,
  },
};

type Contact = {
  id: string;
  name: string;
  number: string;
  aliases?: string[];
  autoSend?: boolean;
};

const settings = ref<EditableSettings | null>(null);
const contacts = ref<Contact[]>([]);
const aiCatalog = ref<AiCatalogEntry[]>([]);
const aiCredentials = ref<{
  deepseek: { configured: boolean };
  openai: { configured: boolean };
} | null>(null);
const error = ref('');
const message = ref('');
const contactMessage = ref('');
const saving = ref(false);
const savingContact = ref(false);
const newContact = reactive({ name: '', number: '', autoSend: false });

const displayName = computed(() => settings.value?.name?.trim() || assistantName.value);
const displayWake = computed(() => settings.value?.wakeWord?.trim() || wakeWord.value || displayName.value);

const modelsForProvider = computed(() => {
  const p = aiCatalog.value.find((x) => x.id === settings.value?.aiProvider);
  return p?.models ?? [];
});

const selectedModelNote = computed(() => {
  const m = modelsForProvider.value.find((x) => x.id === settings.value?.aiModel);
  return m?.notes ?? '';
});

function credentialOk(id: AiProviderId): boolean {
  return Boolean(aiCredentials.value?.[id]?.configured);
}

const numbersText = computed({
  get: () => (settings.value?.authorizedNumbers ?? []).join('\n'),
  set: (value: string) => {
    if (!settings.value) return;
    settings.value.authorizedNumbers = value
      .split(/\r?\n/)
      .map((line) => line.replace(/\D/g, ''))
      .filter(Boolean);
  },
});

async function loadContacts() {
  const res = await fetch('/api/contacts', { credentials: 'include' });
  if (!res.ok) return;
  const data = (await res.json()) as { contacts: Contact[] };
  contacts.value = data.contacts ?? [];
}

onMounted(async () => {
  try {
    const res = await fetch('/api/settings', { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as {
      settings: EditableSettings;
      aiCredentials?: { deepseek: { configured: boolean }; openai: { configured: boolean } };
      aiCatalog?: AiCatalogEntry[];
    };
    aiCredentials.value = data.aiCredentials ?? { deepseek: { configured: false }, openai: { configured: false } };
    aiCatalog.value = data.aiCatalog ?? [];
    settings.value = {
      ...data.settings,
      secondSelfMode: data.settings.secondSelfMode ?? false,
      aiProvider: data.settings.aiProvider ?? 'deepseek',
      aiPreset: data.settings.aiPreset ?? 'balanced',
      aiThinking: data.settings.aiThinking ?? 'enabled',
      aiReasoningEffort: data.settings.aiReasoningEffort ?? 'high',
      aiEffortMode: data.settings.aiEffortMode ?? 'auto',
      aiMaxToolRounds: data.settings.aiMaxToolRounds ?? 5,
    };
    ensureModelForProvider();
    await loadContacts();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Falha ao carregar';
  }
});

function applyPreset(id: Exclude<AiPreset, 'custom'>) {
  if (!settings.value) return;
  Object.assign(settings.value, PRESETS[id]);
}

function markCustom() {
  if (settings.value) settings.value.aiPreset = 'custom';
}

function ensureModelForProvider() {
  if (!settings.value) return;
  const models = modelsForProvider.value;
  if (!models.length) return;
  if (!models.some((m) => m.id === settings.value!.aiModel)) {
    const cat = aiCatalog.value.find((p) => p.id === settings.value!.aiProvider);
    settings.value.aiModel = cat?.defaultModel ?? models[0]!.id;
  }
}

function onProviderChange() {
  markCustom();
  ensureModelForProvider();
}

async function save() {
  if (!settings.value) return;
  saving.value = true;
  message.value = '';
  try {
    const res = await fetch('/api/settings', {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(settings.value),
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { detail?: string };
      throw new Error(data.detail || `HTTP ${res.status}`);
    }
    const data = (await res.json()) as { settings: EditableSettings };
    settings.value = data.settings;
    setAssistantIdentity({
      name: settings.value.name,
      wakeWord: settings.value.wakeWord,
    });
    const savedName = settings.value.name.trim() || assistantName.value;
    message.value = settings.value.secondSelfMode
      ? `Salvo. Modo segundo eu LIGADO — a ${savedName} age com liberdade.`
      : 'Salvo. Modo restrito — sites autorizados e contatos valem.';
  } catch (err) {
    message.value = err instanceof Error ? err.message : 'Falha ao salvar';
  } finally {
    saving.value = false;
  }
}

async function addContact() {
  contactMessage.value = '';
  if (!newContact.name.trim() || !newContact.number.trim()) {
    contactMessage.value = 'Informe nome e número.';
    return;
  }
  savingContact.value = true;
  try {
    const res = await fetch('/api/contacts', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        name: newContact.name.trim(),
        number: newContact.number.replace(/\D/g, ''),
        autoSend: newContact.autoSend,
      }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    newContact.name = '';
    newContact.number = '';
    newContact.autoSend = false;
    await loadContacts();
    contactMessage.value = 'Contato salvo.';
  } catch (err) {
    contactMessage.value = err instanceof Error ? err.message : 'Falha ao salvar contato';
  } finally {
    savingContact.value = false;
  }
}

async function toggleAutoSend(c: Contact) {
  contactMessage.value = '';
  const res = await fetch('/api/contacts', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      name: c.name,
      number: c.number,
      aliases: c.aliases,
      autoSend: !c.autoSend,
    }),
  });
  if (!res.ok) {
    contactMessage.value = `Falha ao atualizar (${res.status})`;
    return;
  }
  await loadContacts();
  contactMessage.value = !c.autoSend
    ? `${c.name}: agora envia sem pedir.`
    : `${c.name}: agora exige confirmação.`;
}

async function removeContact(id: string) {
  await fetch(`/api/contacts/${id}`, { method: 'DELETE', credentials: 'include' });
  await loadContacts();
}
</script>

<style scoped>
.preset-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin: 0.5rem 0 1rem;
}
.preset {
  border: 1px solid color-mix(in srgb, currentColor 22%, transparent);
  background: transparent;
  color: inherit;
  padding: 0.4rem 0.85rem;
  border-radius: 6px;
  cursor: pointer;
}
.preset.active {
  border-color: var(--accent, #2a7);
  background: color-mix(in srgb, var(--accent, #2a7) 14%, transparent);
}
select {
  width: 100%;
  padding: 0.45rem 0.6rem;
  border-radius: 6px;
  border: 1px solid color-mix(in srgb, currentColor 20%, transparent);
  background: inherit;
  color: inherit;
}
.warn {
  border: 1px solid color-mix(in srgb, var(--accent, #c45) 40%, transparent);
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: color-mix(in srgb, var(--accent, #c45) 8%, transparent);
}
.contact-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.75rem 0;
}
.contact-row input {
  flex: 1 1 140px;
}
.contact-list {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0;
}
.contact-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid color-mix(in srgb, currentColor 12%, transparent);
}
.contact-actions {
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
}
.badge {
  display: inline-block;
  margin-left: 0.4rem;
  font-size: 0.75rem;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  background: color-mix(in srgb, #2a7 18%, transparent);
}
.check.compact {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
}
.linkish {
  background: none;
  border: none;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
  opacity: 0.8;
}
</style>
